import { JsonSchema, UISchemaElement } from "@jsonforms/core";
import { createContext } from "react";

/**
 * Context type for the ArrayModalControl component.
 * Provides shared data and functions for child components.
 */
interface ArrayModalControlContextType {
  /**
   * Label for the control.
   */
  label: string;

  /**
   * Stable test-id prefix from the schema path. `label` is a translated
   * item name, so it is not safe to slug into a selector.
   */
  testIdPrefix: string;

  /**
   * Schema for the child items.
   */
  childSchema: JsonSchema;

  /**
   * UI schema for the child items.
   */
  childUiSchema: UISchemaElement;

  /**
   * Additional options for the control.
   */
  options: Record<string, unknown>;

  /**
   * Data array for the control.
   */
  data: Record<string, unknown>[];

  /**
   * Function to render a custom tag for an item.
   */
  renderTag?: (item: Record<string, unknown>) => React.ReactNode;

  /**
   * Function to render a custom subtitle for an item.
   */
  renderSubtitle?: (item: Record<string, unknown>) => React.ReactNode;

  /**
   * Function to handle submission of an item.
   */
  handleSubmit: (itemPath: string, values: unknown) => Promise<void>;

  /**
   * Function to handle deletion of an item.
   */
  handleDelete: (itemPath: string) => Promise<void>;
}

/**
 * React context for the ArrayModalControl component.
 * Provides shared data and functions for child components.
 */
export const ArrayModalControlContext =
  createContext<ArrayModalControlContextType>(undefined!);
